import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import M_EMT_BASIC_01 from './M_EMT_BASIC_01';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import {
  appendCurrentUserToMemberList,
  resolveEmTelUserId,
  resolveMemberUserId,
} from '../ui/emTelUtils';
import {
  useSave1,
  useSearch1,
  useSearch2,
  useSearch3,
} from './M_EMT_BASIC_01_Query';

export default function M_EMT_BASIC_01F() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [emTelList, setEmTelList] = useState([]);
  const [depList, setDepList] = useState([]);
  const [selectedDepCd, setSelectedDepCd] = useState('');

  useEffect(() => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('비상연락망 관리'));
    dispatch(NavBarInfoActions.setBgColor('#FFF'));
  }, [dispatch]);

  const { refetch: refetchEmTelList } = useSearch1({
    depCd: selectedDepCd,
    enabled: !!selectedDepCd,
    onSuccess: (rows) => {
      setEmTelList(rows);
    },
  });

  useSearch2({
    onSuccess: (rows) => {
      const depMap = new Map();

      (rows || [])
        .filter((row) => row.depNmLast && String(row.depNmLast).trim())
        .forEach((row) => {
          if (!row.depCd || depMap.has(row.depCd)) return;
          depMap.set(row.depCd, {
            name: row.depNmLast,
            value: row.depCd,
          });
        });

      const nextDepList = [...depMap.values()];
      setDepList(nextDepList);
      setSelectedDepCd((prev) =>
        prev && nextDepList.some((dep) => dep.value === prev)
          ? prev
          : nextDepList[0]?.value ?? ''
      );
    },
  });

  const { runAsync: fetchDepartmentMembersAsync } = useSearch3();
  const fetchDepartmentMembersAsyncRef = useRef(fetchDepartmentMembersAsync);
  fetchDepartmentMembersAsyncRef.current = fetchDepartmentMembersAsync;

  const fetchDepartmentMembers = useCallback(async () => {
    const rows = await fetchDepartmentMembersAsyncRef.current({});
    const list = Array.isArray(rows) ? rows : [];

    return appendCurrentUserToMemberList(list, userInfo);
  }, [userInfo]);

  const { runAsync: saveEmTelAsync } = useSave1();

  const saveEmTelItems = async (items, emergencyTelYn = 'Y') => {
    if (!userInfo?.custCd) {
      throw new Error('사업장 정보를 확인할 수 없습니다.');
    }

    if (!selectedDepCd) {
      throw new Error('등록할 부서를 선택해주세요.');
    }

    const targets = items
      .map((item) => ({
        targetUserId:
          typeof item === 'string'
            ? item
            : item.targetUserId ||
              resolveEmTelUserId(item) ||
              resolveMemberUserId(item),
        emergencyTelYn,
      }))
      .filter((item) => item.targetUserId);

    if (targets.length === 0) {
      throw new Error(
        emergencyTelYn === 'N'
          ? '해제할 대상이 없습니다.'
          : '등록할 인원이 없습니다.'
      );
    }

    const result = await saveEmTelAsync({
      items: targets,
      depCd: selectedDepCd,
    });
    const failedItems = (result?.data || []).filter((item) => !item?.isSuccess);

    if (failedItems.length > 0) {
      throw new Error(
        failedItems[0]?.message ||
          (emergencyTelYn === 'N'
            ? '비상연락망 해제 중 오류가 발생했습니다.'
            : '비상연락망 등록 중 오류가 발생했습니다.')
      );
    }

    await refetchEmTelList();
    return targets.length;
  };

  const handleAddComplete = async (selectedMembers) => {
    const depName =
      depList.find((dep) => dep.value === selectedDepCd)?.name || '선택한 부서';

    try {
      const count = await saveEmTelItems(selectedMembers, 'Y');

      dispatch(
        showSnackbar({
          message: `${count}명을 ${depName} 비상연락망에 등록했습니다.`,
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '비상연락망 등록 중 오류가 발생했습니다.',
          type: 'warning',
        })
      );
    }
  };

  const handleRemoveComplete = async (contact) => {
    try {
      await saveEmTelItems([contact], 'N');

      dispatch(
        showSnackbar({
          message: '비상연락망에서 해제했습니다.',
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '비상연락망 해제 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
      throw error;
    }
  };

  return (
    <div className="flex h-full flex-col overflow-auto bg-[#f8faf9]">
      <M_EMT_BASIC_01
        emTelList={emTelList}
        depList={depList}
        selectedDepCd={selectedDepCd}
        onSelectDepCd={setSelectedDepCd}
        fetchDepartmentMembers={fetchDepartmentMembers}
        onAddComplete={handleAddComplete}
        onRemoveComplete={handleRemoveComplete}
      />
    </div>
  );
}
